'use client'

import Link from 'next/link'
import { useLandingPageViewmodel } from '@/features/landing/landing-page.hooks'
import { LandingHomeLink } from '@/features/landing/view/LandingHomeLink'
import { LandingLeadForm } from '@/features/landing/view/LandingLeadForm'
import { routePaths } from '@/shared/config/site'
import { Button } from '@/shared/ui/Button'

export function VibecodesLandingPage() {
  const landingPageViewmodel = useLandingPageViewmodel()
  const { content } = landingPageViewmodel.page

  return (
    <div className="page-shell bg-[#050403] text-phosphor-ivory">
      <main className="relative overflow-hidden" id={'content'}>
        <div className="absolute left-5 top-5 z-20 md:left-8 md:top-8">
          <LandingHomeLink className="hover:bg-[#24110c]" />
        </div>
        <section className="relative z-0 isolate px-5 pb-8 pt-20 md:px-8 md:pt-20 lg:px-10">
          <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_68%_35%,rgba(255,207,106,0.18),transparent_34%),linear-gradient(180deg,#160804_0%,#050403_78%)]" />
          <img
            alt={content.visual.alt}
            className="pointer-events-none absolute right-[-14rem] top-16 z-0 h-[52rem] max-w-none rotate-[-8deg] object-contain opacity-72 md:right-[-6rem] lg:right-8"
            decoding="async"
            height={900}
            src={content.visual.src}
            width={1800}
          />
          <div className="relative z-10 mx-auto flex max-w-7xl flex-col justify-start">
            <div className="max-w-4xl border-l-4 border-[#ffcf6a] bg-[#050403]/86 p-5 md:p-7">
              <h1 className="max-w-4xl font-sans text-4xl font-black leading-none text-[#ffcf6a] md:text-6xl lg:text-7xl">
                {content.title}
              </h1>
              <p className="mt-7 max-w-3xl text-xl font-semibold leading-9 text-white md:text-2xl md:leading-10">
                {content.intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="border-[#ffcf6a] bg-[#ffcf6a] text-[#050403] hover:bg-[#f7b955]" size="lg">
                  <a href="#lead">{content.primaryCta}</a>
                </Button>
                <Button asChild className="border-[#ff3d5a] bg-[#170608] text-white hover:bg-[#24090d]" size="lg">
                  <Link href={routePaths.service}>{content.secondaryCta}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
        <section className="relative z-10 mx-auto grid max-w-7xl gap-3 px-5 pb-8 pt-4 md:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:px-10">
          <div className="relative overflow-hidden p-6 text-[#050403] md:p-8">
            <div className="absolute inset-0 bg-[#ffcf6a]/50" />
            <div className="relative z-10">
              <h2 className="max-w-3xl text-4xl font-black leading-none md:text-6xl">{content.closingTitle}</h2>
              <p className="mt-6 max-w-2xl text-base font-semibold leading-8">{content.closingBody}</p>
            </div>
          </div>
          <div className="grid gap-3">
            {content.processItems.map((item, index) => (
              <article key={item} className="grid grid-cols-[3.5rem_minmax(0,1fr)] bg-[#130806]/84 p-5">
                <span className="font-mono text-sm font-semibold text-[#ff3d5a]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="text-base font-semibold leading-7 text-white/78">{item}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="relative z-10 mx-auto grid max-w-7xl gap-5 px-5 py-10 md:px-8 lg:grid-cols-[minmax(0,1fr)_28rem] lg:px-10">
          <div className="grid gap-5">
            <div className="grid gap-3 md:grid-cols-3">
              {content.proofItems.map((item) => (
                <article key={item.title} className="border-t-4 border-[#ffcf6a] bg-[#100907]/88 p-5">
                  <h2 className="text-lg font-black leading-tight text-[#ffcf6a]">{item.title}</h2>
                  <p className="mt-4 text-sm font-medium leading-7 text-white/68">{item.description}</p>
                </article>
              ))}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <TruthList items={content.fitItems} title={content.fitTitle} />
              <TruthList danger items={content.boundaryItems} title={content.boundaryTitle} />
            </div>
          </div>
          <LandingLeadForm className="border-[#ffcf6a]/40 bg-[#120b08]/92" />
        </section>
        <section className="relative z-10 mx-auto flex max-w-7xl flex-col gap-5 px-5 pb-16 md:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <p className="max-w-3xl text-2xl font-black leading-9 text-[#ffcf6a]">{content.closingBody}</p>
          <Button asChild className="border-[#ffcf6a] bg-[#ffcf6a] text-[#050403] hover:bg-[#f7b955]" size="lg">
            <a href="#lead">{content.primaryCta}</a>
          </Button>
        </section>
      </main>
    </div>
  )
}

function TruthList({ danger = false, items, title }: { danger?: boolean; items: string[]; title: string }) {
  return (
    <article className={danger ? 'border-l-4 border-[#ff3d5a] bg-[#16090c]/88 p-5' : 'bg-[#11100d]/88 p-5'}>
      <h2 className={danger ? 'text-xl font-black text-[#ff3d5a]' : 'text-xl font-black text-[#ffcf6a]'}>{title}</h2>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item} className="text-sm font-semibold leading-7 text-white/72">
            {item}
          </li>
        ))}
      </ul>
    </article>
  )
}
